import { useEffect, useState } from 'react';
import { Button, Modal, Space, Tag } from 'antd';
import { MainContainerForAddFallback, StyledTable } from './styled';
import { getColumns } from '../Common/columns';
import { useStores } from '@/hooks/use-stores';
import SpinCentered from '@/comp/SpinCentered';
import { FilterCondition } from './types';
import { useTranslation } from 'react-i18next';
import { addCompressorNameInsetPointsIfNotExists, compressorNamesFromIds, handleSaveScenario } from './Fallbacks.utils';
import { FallbackFilterEnum } from '@/constants/FallbackEnum';
import { useMeasurement } from '@/hooks/useMeasurement';
import { EngineStore } from '@/store/Engine/EngineStore';
import DevicesStore from '@/store/Devices/DevicesStore';
import { Setpoint } from '@/store/Engine/common';


interface AddFallbackProps {
  onClose: () => void;
  rangeParentKey?: string | null;
  onSaveSuccess?: () => void;
  smartRulesData?: Setpoint[];
  appliedFilters?: FilterCondition[];
}

const AddFallback = ({ onClose, rangeParentKey, onSaveSuccess, smartRulesData, appliedFilters: initialAppliedFilters }: AddFallbackProps) => {
  const { engineStore, devicesStore }: { engineStore?: EngineStore; devicesStore?: DevicesStore } = useStores();
  const { t } = useTranslation();
  const { tvalue } = useMeasurement();
  const [tableName, setTableName] = useState('');
  const [appliedFilters] = useState<FilterCondition[]>(initialAppliedFilters || []);
  const [tableData, setTableData] = useState<Setpoint[]>([]);
  const [confirmationModalInfo, setConfirmationModalInfo] = useState({
    open: false,
    modalText: undefined,
    actionHandler: undefined,
  });
  useEffect(() => {
    if (smartRulesData && !rangeParentKey) {
      setTableName(smartRulesData[0]?.name || '');
      setTableData(addCompressorNameInsetPointsIfNotExists(smartRulesData, compressors));
    } else if (rangeParentKey) {
      const compData: Setpoint[] =
        engineStore?.setpointsConfiguration?.getSetpointsByPressureRange(rangeParentKey) || [];
      setTableName(compData[0]?.name || '');
      
      setTableData(addCompressorNameInsetPointsIfNotExists(compData, compressors)); 
    }
  }, [rangeParentKey, smartRulesData, engineStore?.setpointsConfiguration]);

  const columnsToUseForTableView = getColumns(false, t);

  const compressors = devicesStore.compressors;

  const onConfirmationModalOpen = (modalText, actionHandler) => () =>
    setConfirmationModalInfo({ open: true, modalText, actionHandler });

  const onConfirmationModalClose = () =>
    setConfirmationModalInfo({ open: false, modalText: undefined, actionHandler: undefined });

  const handleSaveButtonClick = () => {
    onConfirmationModalOpen(t('confirmSaveScenario'), handleSaveScenarioWrapper)();
  };

  const handleSaveScenarioWrapper = async () => {
    await handleSaveScenario({
      appliedFilters,
      rangeParentKey,
      smartRulesData,
      engineStore,
      devicesStore,
      tvalue,
      t,
      onSaveSuccess,
      onClose,
    });
  };

  if (engineStore?.isLoadingSaveUpdateRemoveFallbackScenario) {
    return <SpinCentered />;
  }

  const hasFiltersApplied = appliedFilters.length > 0;
  return (
    <MainContainerForAddFallback>
      <div className="header-buttons">
        <Button onClick={onClose}>{t('Cancel')}</Button>
        <Button type="primary" onClick={handleSaveButtonClick}>
          {t('saveScenario')}
        </Button>
      </div>
      <div className="content-section">
        <div className="left-panel">
          <div className="filter-header">
            <div className="filter-subtitle">{t('newFallbackScenario')}</div>
            <div className="filter-title">
              {t('scenarioBasedOn')} {tableName}
            </div>
          </div>
        </div>

        <div className="right-panel">
          <div className="details-container">
            <div className="details-header">
              <div className="details-content">
                <div className="details-title">
                  {t('fallbackScenario') + ' ' + (engineStore?.fallbackScenarioNames || '')}
                </div>
              </div>
            </div>

            {hasFiltersApplied && (
              <div className="applied-filters-container">
                <div className="applied-filters-label">
                  <strong>{t('appliedFilters')}:</strong>
                </div>
                <Space wrap>
                  {appliedFilters.map((filter, index) => (
                    <Tag className="applied-filters-tag" key={index}>
                      {filter.field === FallbackFilterEnum.FlowKPI && `${t('flowKPI')}`}
                      {filter.field === FallbackFilterEnum.PressureKPI && `${t('pressureKPI')}`}
                      {filter.field === FallbackFilterEnum.CompressorFailure &&
                        `${t('compressorFailure')}: ${compressorNamesFromIds(filter.value as string[], compressors)}`}
                    </Tag>
                  ))}
                </Space>
              </div>
            )}

            <StyledTable
              columns={columnsToUseForTableView}
              dataSource={tableData}
              pagination={false}
              bordered
              size="small"
            />
          </div>
        </div>
      </div>

      <Modal
        open={!!confirmationModalInfo?.open}
        onOk={confirmationModalInfo?.actionHandler}
        onCancel={onConfirmationModalClose}
        okText={t('ok')}
        cancelText={t('cancel')}
        maskClosable={false}
        closable={false}
      >
        <span>{confirmationModalInfo?.modalText}</span>
      </Modal>
    </MainContainerForAddFallback>
  );
};

export default AddFallback;
